import { z } from 'zod';
import {
  BILLABLE_MODELS,
  VIDEO_BILLABLE_MODELS,
  estimateOperationCredits,
} from '@/lib/credits/catalog';

export const VIDEO_MODEL_IDS = VIDEO_BILLABLE_MODELS.map((model) => model.id);
export type VideoModelId = (typeof VIDEO_MODEL_IDS)[number];

/** Catalog default text-to-video model — cheapest verified path (LTX-2 19B). */
export const DEFAULT_VIDEO_MODEL_ID = 'fal/ltx-2-19b-t2v';

export const VIDEO_ASPECT_RATIOS = ['16:9', '9:16', '1:1', '4:3', '3:4', '3:2', '2:3', '21:9', '9:21'] as const;
export const VIDEO_RESOLUTIONS = ['480p', '720p', '1080p', '4K'] as const;

export const generateVideoCapabilityInputShape = {
  prompt: z.string().trim().min(5).max(4000),
  model: z.string().trim().min(1).default(DEFAULT_VIDEO_MODEL_ID),
  sourceImageUrl: z.string().url().optional(),
  referenceImageUrls: z.array(z.string().url()).max(9).optional(),
  aspectRatio: z.enum(VIDEO_ASPECT_RATIOS).optional(),
  resolution: z.enum(VIDEO_RESOLUTIONS).optional(),
  duration: z.number().int().min(2).max(15).optional(),
  audio: z.boolean().optional(),
  seed: z.number().int().optional(),
  persistGeneratedMedia: z.boolean().optional(),
};

export const generateVideoCapabilitySchema = z.object(generateVideoCapabilityInputShape);
export type GenerateVideoInput = z.infer<typeof generateVideoCapabilitySchema>;

export function estimateVideoGeneration(input: Pick<GenerateVideoInput, 'model' | 'duration' | 'audio' | 'resolution'>) {
  const model = BILLABLE_MODELS[input.model];
  const durationSeconds = Math.min(
    Math.max(1, input.duration ?? model?.defaultDurationSeconds ?? 5),
    model?.maxDurationSeconds ?? 15,
  );
  const audioMultiplier = input.audio && model?.supportsAudio ? 1.25 : 1;
  const resolutionMultiplier = input.resolution === '4K'
    ? 2
    : input.resolution === '1080p'
      ? 1.25
      : 1;

  return estimateOperationCredits({
    operation: 'video',
    modelId: model?.id,
    durationSeconds,
    providerCostUsd: (model?.rawCostUsd ?? 0.12) * durationSeconds * audioMultiplier * resolutionMultiplier,
  });
}
